/**
 * Lightweight client-side checks on captured webcam frames.
 */

/**
 * Analyze a canvas frame for occlusion (covered camera / very dark scene).
 * Returns true when the frame is too dark to proctor reliably.
 */
export function isFrameOccludedFromCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  maxLuminance = 35
): boolean {
  if (width <= 0 || height <= 0) return true

  const sampleW = Math.min(64, width)
  const sampleH = Math.min(48, height)
  const imageData = ctx.getImageData(0, 0, sampleW, sampleH)
  const data = imageData.data
  let sum = 0
  const pixels = data.length / 4
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
  }
  const avg = sum / pixels
  return avg < maxLuminance
}

/**
 * Estimate occlusion from base64 JPEG using a small offscreen canvas.
 */
export async function isFrameOccluded(base64: string, maxLuminance = 35): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image()
    const timer = setTimeout(() => resolve(false), 1500)
    img.onload = () => {
      clearTimeout(timer)
      try {
        const canvas = document.createElement('canvas')
        canvas.width = Math.min(64, img.width)
        canvas.height = Math.min(48, img.height)
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx) {
          resolve(false)
          return
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(isFrameOccludedFromCanvas(ctx, canvas.width, canvas.height, maxLuminance))
      } catch {
        resolve(false)
      }
    }
    img.onerror = () => {
      clearTimeout(timer)
      resolve(false)
    }
    img.src = `data:image/jpeg;base64,${base64}`
  })
}
